import { forwardRef, RefObject } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  DropdownInput,
  DropdownOption,
  CrossOutline,
  ArrowRightOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import { CompanyFeeGroupLabels, CompanyTypeLabels } from 'app/constants/companies';
import { countries } from 'app/constants/countries';

import { useCreateCompanyStore } from 'app/store';
import useFetchPartners from 'app/hooks/useFetchPartners';

import { companyDetailsFormSchema } from 'app/utils/schemas';

import { CompanyDetailsFormFields, CompanyFormProps } from '../types';

const CompanyDetailsForm = forwardRef<HTMLElement, CompanyFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const companyData = useCreateCompanyStore((state) => state.companyData);
    const setCompanyData = useCreateCompanyStore((state) => state.setCompanyData);

    const {
      data: partnersData,
      isLoading: isLoadingPartners,
      isError: isPartnersError,
    } = useFetchPartners();

    const {
      register,
      control,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      formState: { errors, isValid },
    } = useForm<CompanyDetailsFormFields>({
      mode: 'onBlur',
      defaultValues: companyData as CompanyDetailsFormFields,
      resolver: yupResolver(companyDetailsFormSchema),
    });

    const watchName = watch('name');
    const watchDisplayName = watch('displayName');
    const watchCompanyNumber = watch('companyNumber');
    const watchAddressLine1 = watch('addressLine1');
    const watchAddressLine2 = watch('addressLine2');
    const watchCity = watch('city');
    const watchPostcode = watch('postcode');

    const countryOptions: DropdownOption[] = countries
      .filter((country) => country.code === 'GB')
      .map((country) => ({
        value: country.code,
        text: country.details.name,
      }));

    const feeGroupOptions: DropdownOption[] = Object.entries(CompanyFeeGroupLabels).map(
      ([value, text]) => ({
        value,
        text,
      })
    );

    const companyTypeOptions: DropdownOption[] = Object.entries(CompanyTypeLabels).map(
      ([value, text]) => ({
        value,
        text,
      })
    );

    const partnerOptions: DropdownOption[] = (partnersData?.list ?? []).map((partner) => ({
      value: partner.id,
      text: partner.name,
    }));

    const resetAndFocusField = (field: keyof CompanyDetailsFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const handleNextStep = (data: CompanyDetailsFormFields) => {
      setCompanyData(data);
      onStepAction();
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setCompanyData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.name?.message} isRequired>
              <FormLabel>Company name</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter company name" {...register('name')} />
                {watchName && (
                  <InputRightElement
                    h="full"
                    cursor="pointer"
                    onClick={() => resetAndFocusField('name')}
                  >
                    <CrossOutline color="brand.darkGray" boxSize="24px" />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors?.displayName?.message}>
              <FormLabel>Company display name</FormLabel>
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Enter company display name"
                  {...register('displayName')}
                />
                {watchDisplayName && (
                  <InputRightElement
                    h="full"
                    cursor="pointer"
                    onClick={() => resetAndFocusField('displayName')}
                  >
                    <CrossOutline color="brand.darkGray" boxSize="24px" />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.displayName?.message}</FormErrorMessage>
            </FormControl>
          </HStack>
          <FormControl isInvalid={!!errors?.companyNumber?.message} isRequired>
            <FormLabel>Company number</FormLabel>
            <InputGroup>
              <Input
                type="text"
                placeholder="Enter company number"
                {...register('companyNumber')}
              />
              {watchCompanyNumber && (
                <InputRightElement
                  h="full"
                  cursor="pointer"
                  onClick={() => resetAndFocusField('companyNumber')}
                >
                  <CrossOutline color="brand.darkGray" boxSize="24px" />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.companyNumber?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.addressLine1?.message} isRequired>
            <FormLabel>Address line 1</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter address" {...register('addressLine1')} />
              {watchAddressLine1 && (
                <InputRightElement
                  h="full"
                  cursor="pointer"
                  onClick={() => resetAndFocusField('addressLine1')}
                >
                  <CrossOutline color="brand.darkGray" boxSize="24px" />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.addressLine1?.message}</FormErrorMessage>
          </FormControl>
          <FormControl>
            <FormLabel>Address line 2</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter address" {...register('addressLine2')} />
              {watchAddressLine2 && (
                <InputRightElement
                  h="full"
                  cursor="pointer"
                  onClick={() => resetAndFocusField('addressLine2')}
                >
                  <CrossOutline color="brand.darkGray" boxSize="24px" />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.city?.message} isRequired>
              <FormLabel>Town/City</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter town/city" {...register('city')} />
                {watchCity && (
                  <InputRightElement
                    h="full"
                    cursor="pointer"
                    onClick={() => resetAndFocusField('city')}
                  >
                    <CrossOutline color="brand.darkGray" boxSize="24px" />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.city?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors?.postcode?.message} isRequired>
              <FormLabel>Postcode</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter postcode" {...register('postcode')} />
                {watchPostcode && (
                  <InputRightElement
                    h="full"
                    cursor="pointer"
                    onClick={() => resetAndFocusField('postcode')}
                  >
                    <CrossOutline color="brand.darkGray" boxSize="24px" />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.postcode?.message}</FormErrorMessage>
            </FormControl>
          </HStack>
          <FormControl isInvalid={!!errors?.countryCode?.message} isRequired>
            <FormLabel>Country</FormLabel>
            <Controller
              control={control}
              name="countryCode"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a country"
                  defaultValue={countryOptions.find((country) => country.value === value)}
                  options={countryOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.countryCode?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.feeGroup?.message} isRequired>
            <FormLabel>FX Fee group</FormLabel>
            <Controller
              control={control}
              name="feeGroup"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a FX fee group option"
                  defaultValue={feeGroupOptions.find((feeGroup) => feeGroup.value === value)}
                  options={feeGroupOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.feeGroup?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.type?.message} isRequired isDisabled>
            <FormLabel>Company type</FormLabel>
            <Controller
              control={control}
              name="type"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a company type"
                  defaultValue={companyTypeOptions.find((feeGroup) => feeGroup.value === value)}
                  options={companyTypeOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.type?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={isPartnersError}>
            <FormLabel>Partner</FormLabel>
            <Controller
              control={control}
              name="partnerId"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder={
                    isLoadingPartners ? 'Loading partners...' : 'Select a partner (optional)'
                  }
                  defaultValue={partnerOptions.find((p) => p.value === value)}
                  options={partnerOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                  isDisabled={isLoadingPartners || isPartnersError}
                />
              )}
            />
            {isPartnersError && (
              <Text textStyle="extraSmall" color="system.error" mt="4px">
                Failed to load partners. You can still create the company without a partner.
              </Text>
            )}
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

CompanyDetailsForm.displayName = 'CompanyDetailsForm';

export default CompanyDetailsForm;
